import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../auth/AuthContext';
import EmailVerificationBanner from './EmailVerificationBanner';
import LanguageSwitcher from '../LanguageSwitcher';

const NAV_ITEMS = [
  { id: 'queue',     path: '/admin/queue',                    icon: '📋', roles: ['OWNER', 'MANAGER', 'STAFF'] },
  { id: 'shops',     path: '/admin/settings', state: { tab: 'shops' },     icon: '🏪', roles: ['OWNER', 'MANAGER'] },
  { id: 'services',  path: '/admin/settings', state: { tab: 'services' },  icon: '🏷️', roles: ['OWNER', 'MANAGER'] },
  { id: 'hours',     path: '/admin/settings', state: { tab: 'hours' },     icon: '🕐', roles: ['OWNER', 'MANAGER', 'STAFF'] },
  { id: 'qr',        path: '/admin/settings', state: { tab: 'qr' },        icon: '📱', roles: ['OWNER', 'MANAGER', 'STAFF'] },
  { id: 'employees', path: '/admin/settings', state: { tab: 'employees' }, icon: '👥', roles: ['OWNER', 'MANAGER'] },
  { id: 'stats',     path: '/admin/settings', state: { tab: 'stats' },     icon: '📈', roles: ['OWNER', 'MANAGER'] },
  { id: 'analyticsBiz', path: '/admin/analytics',                           icon: '📉', roles: ['OWNER', 'MANAGER'] },
  { id: 'referrals', path: '/admin/settings', state: { tab: 'referrals' }, icon: '🎁', roles: ['OWNER', 'MANAGER'] },
  { id: 'billing',   path: '/admin/settings', state: { tab: 'billing' },   icon: '💳', roles: ['OWNER'] },
  { id: 'account',   path: '/admin/settings', state: { tab: 'account' },   icon: '⚙️', roles: ['OWNER', 'MANAGER', 'STAFF'] },
  { id: 'systemHealth', path: '/admin/monitor/system',   icon: '❤️', roles: ['ADMIN'] },
  { id: 'businessMon',  path: '/admin/monitor/business', icon: '📊', roles: ['ADMIN'] },
  { id: 'analytics',    path: '/admin/monitor/analytics', icon: '📉', roles: ['ADMIN'] },
  { id: 'tenantsMon',   path: '/admin/monitor/tenants',  icon: '🏢', roles: ['ADMIN'] },
  { id: 'sessionsMon',  path: '/admin/monitor/sessions', icon: '🔐', roles: ['ADMIN'] },
  { id: 'accountMgmt',  path: '/admin/monitor/account',  icon: '👤', roles: ['ADMIN'] },
  { id: 'queueInspect', path: '/admin/monitor/queues',   icon: '🔍', roles: ['ADMIN'] },
  { id: 'deadLetter',   path: '/admin/monitor/dead-letter', icon: '💀', roles: ['ADMIN'] },
];

function InfoTip({ text, sidebarWidth }) {
  const { t } = useTranslation();
  const [show, setShow] = useState(false);
  const btnRef = useState(null);
  const [pos, setPos] = useState({ top: 0 });

  const handleEnter = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setPos({ top: rect.top + rect.height / 2 });
    setShow(true);
  };

  return (
    <>
      <button
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleEnter(e); setShow(v => !v); }}
        onMouseEnter={handleEnter}
        onMouseLeave={() => setShow(false)}
        style={s.infoBtn}
        aria-label={t('sidebar.infoAria')}
      >
        i
      </button>
      {show && (
        <span style={{ ...s.tooltip, top: pos.top, left: sidebarWidth + 8, transform: 'translateY(-50%)' }}>{text}</span>
      )}
    </>
  );
}

export default function Sidebar({ children }) {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const role = user?.role;
  const visible = NAV_ITEMS.filter((item) => item.roles.includes(role));

  const isActive = (item) => {
    if (item.state?.tab) {
      return location.pathname === '/admin/settings' && location.state?.tab === item.state.tab;
    }
    return location.pathname === item.path;
  };

  const navContent = (
    <>
      <div style={s.brand(collapsed)}>
        <span style={s.logo}>vQ</span>
        {!collapsed && <span style={s.logoText}>vQueue</span>}
      </div>

      <nav style={s.nav}>
        {visible.map((item) => (
          <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
            <Link
              to={item.path}
              state={item.state}
              onClick={() => setMobileOpen(false)}
              style={{ ...s.navItem(isActive(item), collapsed), flex: 1 }}
            >
              <span style={s.navIcon}>{item.icon}</span>
              {!collapsed && <span style={s.navLabel}>{t(`sidebar.${item.id}`)}</span>}
            </Link>
            {!collapsed && <InfoTip text={t(`sidebar.info.${item.id}`)} sidebarWidth={SIDEBAR_W} />}
          </div>
        ))}
      </nav>

      <div style={s.bottom}>
        <div style={s.langRow(collapsed)}>
          <LanguageSwitcher placement="up" compact={collapsed} />
        </div>
        {!collapsed && (
          <div style={s.userInfo}>
            <div style={s.avatar}>{(user?.email || '?')[0].toUpperCase()}</div>
            <div style={s.userMeta}>
              <div style={s.userEmail}>{user?.email}</div>
              <div style={s.userRole}>{role}</div>
            </div>
          </div>
        )}
        <button onClick={logout} style={s.logoutBtn(collapsed)}>
          {collapsed ? '🚪' : t('sidebar.logout')}
        </button>
      </div>
    </>
  );

  return (
    <div style={s.layout}>
      {/* Mobile hamburger */}
      <button
        data-sidebar-hamburger
        style={s.hamburger}
        onClick={() => setMobileOpen(!mobileOpen)}
        aria-label={t('sidebar.menuAria')}
      >
        <span style={s.hamburgerLine} /><span style={s.hamburgerLine} /><span style={s.hamburgerLine} />
      </button>

      {/* Mobile overlay */}
      {mobileOpen && <div style={s.overlay} onClick={() => setMobileOpen(false)} />}

      {/* Sidebar */}
      <aside
        data-sidebar-aside
        data-mobile-open={mobileOpen ? 'true' : 'false'}
        style={s.sidebar(collapsed, mobileOpen)}
      >
        <button style={s.collapseBtn} onClick={() => setCollapsed(!collapsed)} aria-label="Toggle sidebar">
          {collapsed ? '→' : '←'}
        </button>
        {navContent}
      </aside>

      {/* Main content */}
      <main data-sidebar-main style={s.main(collapsed)}>
        <EmailVerificationBanner />
        {children}
      </main>

      <style>{CSS}</style>
    </div>
  );
}

const SIDEBAR_W = 240;
const SIDEBAR_COLLAPSED = 64;

const s = {
  layout: { display: 'flex', minHeight: '100dvh', background: '#0F172A', fontFamily: 'system-ui, sans-serif' },
  sidebar: (collapsed, mobileOpen) => ({
    width: collapsed ? SIDEBAR_COLLAPSED : SIDEBAR_W,
    minHeight: '100dvh',
    background: '#1E293B',
    borderRight: '1px solid #334155',
    display: 'flex',
    flexDirection: 'column',
    position: 'fixed',
    top: 0,
    left: mobileOpen ? 0 : undefined,
    zIndex: 200,
    transition: 'width 0.2s ease',
    // overflow: visible so the collapse-toggle button that protrudes
    // 12px to the right of the sidebar is fully visible instead of
    // being clipped in half. Nav items that overflow vertically are
    // already handled by the inner <nav>'s own overflowY: auto.
    overflow: 'visible',
  }),
  main: (collapsed) => ({
    flex: 1,
    marginLeft: collapsed ? SIDEBAR_COLLAPSED : SIDEBAR_W,
    minHeight: '100dvh',
    transition: 'margin-left 0.2s ease',
  }),
  brand: (collapsed) => ({
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: collapsed ? '1rem 0' : '1rem 1.25rem',
    justifyContent: collapsed ? 'center' : 'flex-start',
    borderBottom: '1px solid #334155',
    minHeight: 56,
  }),
  logo: {
    background: 'linear-gradient(135deg, #7C3AED, #06B6D4)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    fontWeight: 800,
    fontSize: '1.3rem',
  },
  logoText: {
    fontWeight: 800,
    fontSize: '1.1rem',
    color: '#F8FAFC',
    whiteSpace: 'nowrap',
  },
  nav: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    padding: '0.75rem 0.5rem',
    overflowY: 'auto',
  },
  navItem: (active, collapsed) => ({
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: collapsed ? '0.6rem 0' : '0.6rem 0.75rem',
    justifyContent: collapsed ? 'center' : 'flex-start',
    borderRadius: 8,
    textDecoration: 'none',
    fontSize: '0.88rem',
    fontWeight: active ? 600 : 500,
    color: active ? '#F8FAFC' : '#94A3B8',
    background: active ? 'rgba(124,58,237,0.15)' : 'transparent',
    transition: 'background 0.15s, color 0.15s',
    whiteSpace: 'nowrap',
  }),
  navIcon: { fontSize: '1.1rem', width: 24, textAlign: 'center', flexShrink: 0 },
  navLabel: { overflow: 'hidden', textOverflow: 'ellipsis', flex: 1 },
  infoBtn: {
    width: 18, height: 18, borderRadius: '50%', border: '1px solid #475569',
    background: 'transparent', color: '#64748B', fontSize: '0.6rem', fontWeight: 700,
    fontStyle: 'italic', cursor: 'pointer', display: 'inline-flex',
    alignItems: 'center', justifyContent: 'center', flexShrink: 0,
    marginRight: 6, transition: 'border-color 0.15s, color 0.15s',
  },
  tooltip: {
    position: 'fixed',
    background: '#0F172A', border: '1px solid #475569', borderRadius: 8,
    padding: '8px 12px', fontSize: '0.78rem', color: '#CBD5E1', lineHeight: 1.5,
    width: 220, zIndex: 9999, boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
    whiteSpace: 'normal', pointerEvents: 'none',
  },
  bottom: {
    borderTop: '1px solid #334155',
    padding: '0.75rem',
  },
  langRow: (collapsed) => ({
    display: 'flex',
    justifyContent: collapsed ? 'center' : 'flex-end',
    marginBottom: '0.5rem',
  }),
  userInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: '0.5rem',
  },
  avatar: {
    width: 32,
    height: 32,
    borderRadius: '50%',
    background: '#334155',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '0.85rem',
    color: '#94A3B8',
    fontWeight: 700,
    flexShrink: 0,
  },
  userMeta: { overflow: 'hidden' },
  userEmail: { fontSize: '0.78rem', color: '#F8FAFC', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  userRole: { fontSize: '0.7rem', color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.04em' },
  logoutBtn: (collapsed) => ({
    width: '100%',
    padding: '0.5rem',
    borderRadius: 6,
    border: '1px solid #334155',
    background: 'transparent',
    color: '#94A3B8',
    fontSize: '0.82rem',
    fontWeight: 600,
    cursor: 'pointer',
    textAlign: 'center',
  }),
  collapseBtn: {
    position: 'absolute',
    top: 16,
    right: -12,
    width: 24,
    height: 24,
    borderRadius: '50%',
    border: '1px solid #334155',
    background: '#1E293B',
    color: '#94A3B8',
    fontSize: '0.7rem',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10,
  },
  hamburger: {
    display: 'none',
    position: 'fixed',
    top: 12,
    left: 12,
    zIndex: 300,
    background: '#1E293B',
    border: '1px solid #334155',
    borderRadius: 8,
    padding: 8,
    cursor: 'pointer',
    flexDirection: 'column',
    gap: 4,
  },
  hamburgerLine: { display: 'block', width: 20, height: 2, background: '#94A3B8', borderRadius: 2 },
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.5)',
    zIndex: 150,
  },
};

const CSS = `
  @media (max-width: 768px) {
    main[data-sidebar-main] {
      margin-left: 0 !important;
      /* Clear the fixed hamburger button (top:12 left:12, ~36x36) so
         pages whose first element sits at the top-left (connection
         status, title, back link) aren't obscured. */
      padding-top: 56px;
    }
    button[data-sidebar-hamburger] {
      display: flex !important;
    }
    aside[data-sidebar-aside] {
      transform: translateX(-100%);
      transition: transform 0.2s ease, width 0.2s ease;
    }
    aside[data-sidebar-aside][data-mobile-open="true"] {
      transform: translateX(0);
    }
  }
`;
